import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Chip, IconButton, Snackbar, Tooltip } from "@mui/material";
import { NotificationAddOutlined as BellPlusIcon } from "../icons";
import { NotificationsActive as BellRingIcon } from "../icons";
import { TimerOutlined as TimerIcon } from "../icons";
import { auth } from "../firebase";
import { brandColors } from "../theme";
import { T } from "../routes/Translate";
import { getItemDeadline, toggleItemFollow, useIsItemFollowed } from "../utils/itemFollows";

const HOUR_MS = 60 * 60 * 1000;
const DAY_MS = 24 * HOUR_MS;

const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const daysBetween = (from, to) => Math.round((startOfDay(to) - startOfDay(from)) / DAY_MS);

// Texto de cuánto falta: { label, urgent }. Eventos cuentan hasta que
// empiezan; ofertas hasta que vence el cupón.
const describeDeadline = (type, deadline, now) => {
  const left = deadline - now;
  const days = daysBetween(now, deadline);
  if (type === "event") {
    if (left <= 0 || days === 0) return { label: <T>¡Es hoy!</T>, urgent: true };
    if (left < DAY_MS) return { label: <><T>¡Falta poco!</T> {Math.ceil(left / HOUR_MS)} h</>, urgent: true };
    if (days === 1) return { label: <T>Es mañana</T>, urgent: true };
    return { label: <><T>Faltan</T> {days} <T>días</T></>, urgent: false };
  }
  if (days === 0) return { label: <T>¡Termina hoy!</T>, urgent: true };
  if (days === 1) return { label: <T>Termina mañana</T>, urgent: true };
  return { label: <><T>Termina en</T> {days} <T>días</T></>, urgent: false };
};

// Chip "Faltan 3 días" / "¡Falta poco! 5 h": solo para quien sigue la
// publicación (ofertas con vencimiento y eventos).
export const FollowItemCountdown = ({ type, item, sx }) => {
  const isFollowed = useIsItemFollowed(type, item?.id);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!isFollowed) return undefined;
    const id = setInterval(() => setNow(new Date()), 60 * 1000);
    return () => clearInterval(id);
  }, [isFollowed]);
  if (!isFollowed || !item) return null;
  const deadline = getItemDeadline(type, item, now);
  if (!deadline) return null;
  const { label, urgent } = describeDeadline(type, deadline, now);
  return (
    <Chip
      size="small"
      icon={<TimerIcon sx={{ fontSize: 16 }} />}
      label={label}
      sx={{
        fontWeight: 700,
        alignSelf: "flex-start",
        ...(urgent
          ? { bgcolor: "warning.main", color: "warning.contrastText", "& .MuiChip-icon": { color: "inherit" } }
          : { bgcolor: "action.selected", color: "text.primary", "& .MuiChip-icon": { color: "inherit" } }),
        ...sx,
      }}
    />
  );
};

const FOLLOW_HINT = {
  offer: "Te avisamos si baja de precio o si el cupón está por vencer.",
  event: "Te avisamos cuando falte poco o si cambia la fecha.",
  car: "Te avisamos si baja de precio.",
  place: "Te avisamos si baja de precio.",
};

// Campana para seguir una publicación. variant="icon" va en la barra de las
// tarjetas (al lado de Guardar); variant="button" en las fichas de detalle.
const FollowItemButton = ({ type, item, variant = "icon", sx }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isFollowed = useIsItemFollowed(type, item?.id);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const user = auth.currentUser;
  const isOwner = user && item && (item.uid || item.userId) === user.uid;

  if (!item?.id || isOwner || (variant === "icon" && !user)) return null;

  const handleClick = async (e) => {
    e.stopPropagation();
    if (!user) {
      navigate("/login", { state: { from: location } });
      return;
    }
    if (busy) return;
    setBusy(true);
    try {
      await toggleItemFollow({
        type,
        itemId: item.id,
        title: item.product || item.title || item.name,
        isFollowed,
      });
      setToast(isFollowed ? "Dejaste de seguir esta publicación." : FOLLOW_HINT[type]);
    } catch (error) {
      console.error("Error al seguir la publicación:", error);
    } finally {
      setBusy(false);
    }
  };

  const label = isFollowed ? "Dejar de seguir" : "Seguir publicación";
  const snackbar = (
    <Snackbar
      open={!!toast}
      autoHideDuration={3500}
      onClose={() => setToast("")}
      message={<T>{toast}</T>}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      sx={{ bottom: { xs: 80, sm: 24 } }}
    />
  );

  if (variant === "button") {
    return (
      <>
        <Button
          variant={isFollowed ? "outlined" : "contained"}
          disableElevation
          disabled={busy}
          onClick={handleClick}
          startIcon={isFollowed ? <BellRingIcon /> : <BellPlusIcon />}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 999,
            ...(isFollowed
              ? { color: "text.primary", borderColor: "divider", "&:hover": { borderColor: "text.primary", bgcolor: "action.hover", color: "text.primary" } }
              : {
                  bgcolor: brandColors.accent,
                  color: brandColors.mode === "light" ? "#ffffff" : "#0a0a1a",
                  "&:hover": { bgcolor: brandColors.accent, filter: "brightness(1.08)" },
                }),
            ...sx,
          }}
        >
          {isFollowed ? <T>Siguiendo</T> : <T>Seguir publicación</T>}
        </Button>
        {snackbar}
      </>
    );
  }

  return (
    <>
      <Tooltip title={<T>{label}</T>}>
        <IconButton
          onClick={handleClick}
          disabled={busy}
          aria-label={label}
          aria-pressed={isFollowed}
          sx={{ p: { xs: 0.5, sm: 1 }, ...(isFollowed && { color: brandColors.accent }), ...sx }}
        >
          {isFollowed ? <BellRingIcon /> : <BellPlusIcon />}
        </IconButton>
      </Tooltip>
      {snackbar}
    </>
  );
};

export default FollowItemButton;
